import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { salaryGradesApi } from '../../plantilla/plantillaApi';
import { PositionModal } from '../components/PositionModal';
import { positionsApi } from '../positionsApi';
import type { PositionListItem } from '../positionTypes';

/** The catalog of position titles the staffing pattern draws its posts from. */
export function PositionsIndexPage() {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<PositionListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    position: PositionListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    WHAT THIS MODULE CANNOT BE USED WITHOUT — the published salary schedule.

    The grade is REQUIRED on a designation and the schedule is its only source, so no title can
    be created before the schedule has a row. `<PositionModal>` already said so, which was right
    about the rule and wrong about the place: a modal can only say it to somebody who has
    already opened it, and the reader who most needs telling is the one looking at an empty list
    wondering why Create is disabled. Its own notice stays, because it is the only thing that
    covers an EDIT opened against an emptied schedule — which this banner does not speak to.

    This is the deepest link in the Human Resources chain: Personnel needs a vacant post, a post
    needs a position and a unit, and a position needs a grade. Each page states its own and
    links to it.

    Seeded TRUE and a failed feed leaves it true, for the reason the shared shell documents.
  */
  const [hasSalarySchedule, setHasSalarySchedule] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    salaryGradesApi
      .options(controller.signal)
      .then((grades) => {
        if (!controller.signal.aborted) setHasSalarySchedule(grades.length > 0);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const fetchPositions = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      positionsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          type: query.facets.type?.join(','),
          usage: query.facets.usage?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (position: PositionListItem): MenuItem[] => [
      {
        key: `restore-${position.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'positions.delete',
        disabledReason: position.isActive ? 'This position is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', position }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await positionsApi.deactivate(confirming.position.id);
      else await positionsApi.activate(confirming.position.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PositionListItem>
        module="positions"
        // Reached through Organization → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Organization',
          { label: 'Settings', to: '/organization/settings' },
          'Positions',
        ]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'type', label: 'Type' },
          /*
            Gated, unlike the two above it: "used" and "unused" are counted from the staffing
            pattern, which is another module's records. Status and salary type are the catalog's
            own fields, where `positions.read` already covers them.
          */
          { key: 'usage', label: 'Usage', permission: 'plantilla.read' },
        ]}
        auditEntityTypes={['Position']}
        rowKey={(position) => position.id}
        fetch={fetchPositions}
        reloadKey={reloadKey}
        searchPlaceholder="Search titles or codes"
        emptyMessage="No positions yet. Add the designations this organization uses."
        /*
          See `hasSalarySchedule`. `positions.create` implies `salary-grades.read` — the form
          binds the grade picker to the schedule — so a reader who could create a title gets the
          link, and one who could not is still told what is missing.
        */
        prerequisites={[
          {
            label: 'the salary schedule',
            met: hasSalarySchedule,
            to: '/organization/settings/salary-grades',
            permission: 'salary-grades.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="positions.create"
        /*
          The anchor opens the title rather than its edit form: the posts drawn from it are
          the substance here, and they live on the profile. `openPermission` is the read key
          because that is what opening it actually needs.
        */
        onRowOpen={(position) => navigate(`/organization/settings/positions/${position.id}`)}
        openPermission="positions.read"
        rowActions={rowActions}
        onEdit={(position) => setEditing(position)}
        editPermission="positions.update"
        onDelete={(position) => setConfirming({ kind: 'deactivate', position })}
        deletePermission="positions.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(position) =>
          position.isActive ? null : 'This position is already deleted.'
        }
        onClearData={() => positionsApi.clear()}
        clearDataPermission="positions.clear-data"
        otherInfo={(position) => position}
      />

      {creating || editing !== null ? (
        <PositionModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete position' : 'Restore position'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`position-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.position.title}" stops being offered when a post is authorized.`
                  : `"${confirming.position.title}" is offered again when a post is authorized.`}
              </p>

              {/*
                The count is the consequence worth stating, and it is stated before the tick:
                deleting a title in wide use is a different decision from deleting one nothing
                has ever been drawn from.
              */}
              {confirming.kind === 'deactivate' && confirming.position.postCount > 0 ? (
                <p>
                  {confirming.position.postCount} post
                  {confirming.position.postCount === 1 ? '' : 's'} in the staffing pattern
                  {confirming.position.postCount === 1 ? ' was' : ' were'} drawn from it. They are
                  not deleted and keep the title they were authorized under — each records its own
                  snapshot rather than reading this row.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted position can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<PositionListItem>[] = [
  {
    key: 'title',
    header: 'Title',
    width: 300,
    flex: true,
    sortable: true,
    render: (position) => <TextCell value={position.title} />,
  },
  {
    key: 'code',
    header: 'Code',
    width: 140,
    sortable: true,
    summary: true,
    render: (position) => <TextCell value={position.code} mono />,
  },
  {
    key: 'salaryGrade',
    header: 'Grade',
    width: 150,
    sortable: true,
    summary: true,
    render: (position) => <TextCell value={position.salaryGradeLabel} />,
  },
  {
    key: 'postCount',
    header: 'Posts',
    width: 100,
    align: 'right',
    sortable: true,
    summary: true,
    // The value is counted from the staffing pattern, so it is more privileged than the row
    // carrying it — the catalog's own fields beside it are deliberately ungated.
    permission: 'plantilla.read',
    render: (position) => <TextCell value={String(position.postCount)} mono numeric />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 260,
    render: (position) => <TextCell value={position.description} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (position) =>
      position.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
